import { assertPresent, isPresent, parseOptionsValuesOrFunc } from '@luminovo/commons';
import {
    AppLinkOptions,
    asLinkProps,
    colorSystem,
    isOwnClick,
    linkable,
    Text,
    useBuildHref,
    useNavigate,
} from '@luminovo/design-system';
import { TableRow as MuiTableRow, styled, TableCell } from '@mui/material';
import { shallow, useSelector } from '@tanstack/react-store';
import React, { type JSX } from 'react';
import { TableComponents } from 'react-virtuoso';

import { useRowHoverSelection } from './Selection';
import { flexRender } from './tanstackPrimitives';
import { Cell, Row, RowData } from './tanstackTypes';
import { ColumnRenderOptionsState, DisplayFormat, EmptyCell, RenderType, TanStackTableContext } from './type';
import { getCommonPinningStyles } from './utils';

/** Marks the cells of columns with `enableOnRowClick: false`: the row ignores clicks that start in them. */
const DISABLE_ROW_CLICK_ATTRIBUTE = 'data-disable-row-click';

export const DefaultTableRow: TableComponents<unknown, TanStackTableContext>['TableRow'] = ({ context, ...props }) => {
    const { table, TableRow = MuiTableRow } = assertPresent(context);
    const { rows } = table.getRowModel();

    const row = rows[props['data-index']];
    // Its cells show its own selection; `TanStackTable` re-renders every row on the other slices, expansion included.
    useSelector(
        table.store,
        () => ({
            isSelected: row?.getIsSelected(),
            isSomeSelected: row?.getIsSomeSelected(),
            isAllSubRowsSelected: row?.getIsAllSubRowsSelected(),
        }),
        { compare: shallow },
    );
    const columnRenderOptions = table.options.meta?.columnRenderOptions ?? {};
    const displayFormat = table.options.meta?.displayFormat;

    // Row can be undefined if data-index is out of bounds (edge case during virtualization)
    if (!isPresent(row)) {
        return <TableRow {...props} />;
    }

    const rowRoute = table.options.meta?.getRowRoute?.(row, table) ?? null;
    const onRowClick = table.options.meta?.onRowClick;
    const visibleCells = row.getVisibleCells();
    const linkCell = rowRoute === null ? undefined : findLinkCell(visibleCells);

    const cells = visibleCells.map((cell, idx) => (
        <InnerTableCell
            key={cell.id}
            cell={cell}
            link={rowRoute !== null && cell === linkCell ? rowRoute : undefined}
            sharedContext={table.options.meta?.sharedContext}
            columnRenderOptions={columnRenderOptions}
            displayFormat={displayFormat}
            isFirstCell={idx === 0}
        />
    ));

    return (
        <TableRowWithHover
            TableRow={TableRow}
            row={row}
            props={props}
            rowRoute={rowRoute}
            // A row with a route is a link: `getRowRoute` takes precedence over `onRowClick`.
            onRowClick={rowRoute === null && onRowClick ? () => onRowClick(row, table) : undefined}
        >
            {cells}
        </TableRowWithHover>
    );
};

/** The first visible cell that takes row clicks, preferring one whose column is marked `rowLink`. */
function findLinkCell<TData extends RowData>(cells: Cell<TData, unknown>[]): Cell<TData, unknown> | undefined {
    const canHoldLink = (cell: Cell<TData, unknown>) => {
        const meta = cell.column.columnDef.meta;
        return meta?.enableOnRowClick !== false && !meta?.disableTableCell;
    };
    return cells.find((cell) => canHoldLink(cell) && cell.column.columnDef.meta?.rowLink) ?? cells.find(canHoldLink);
}

/** Whether a mouse event in a linked row is the row's own: not on a control, in a portal or in an opted-out cell. */
function isRowEvent(event: React.MouseEvent<HTMLElement>): boolean {
    if (event.defaultPrevented || !isOwnClick(event)) {
        return false;
    }
    const optedOutCell = (event.target as Element).closest(`[${DISABLE_ROW_CLICK_ATTRIBUTE}]`);
    return optedOutCell === null || !event.currentTarget.contains(optedOutCell);
}

/** Whether text in the row is selected: a plain click then ends the selection rather than navigating. */
function hasSelectionIn(row: Element): boolean {
    const selection = window.getSelection();
    return (
        isPresent(selection) &&
        !selection.isCollapsed &&
        (row.contains(selection.anchorNode) || row.contains(selection.focusNode))
    );
}

function opensNewTab(event: React.MouseEvent): boolean {
    return (
        event.button === 1 ||
        (event.button === 0 && !event.altKey && (event.metaKey || event.ctrlKey || event.shiftKey))
    );
}

/** Makes the rest of a linked row act like its link for the mouse; Alt, which downloads a link, does nothing. */
function useRowLinkHandlers(rowRoute: AppLinkOptions | null): Partial<React.ComponentProps<typeof MuiTableRow>> {
    const navigate = useNavigate();
    const buildHref = useBuildHref();
    if (rowRoute === null) {
        return {};
    }
    const openInNewTab = () => window.open(buildHref(rowRoute), '_blank', 'noopener');
    return {
        onClick: (event) => {
            if (event.button !== 0 || event.altKey || !isRowEvent(event)) {
                return;
            }
            if (opensNewTab(event)) {
                openInNewTab();
            } else if (!hasSelectionIn(event.currentTarget)) {
                navigate(rowRoute);
            }
        },
        onAuxClick: (event) => {
            if (event.button === 1 && isRowEvent(event)) {
                openInNewTab();
            }
        },
        onMouseDown: (event) => {
            // Off a link the press autoscrolls (middle, Windows), shift-selects, or selects cells (ctrl/cmd, Firefox).
            // Any of those would also leave a selection that the click can't tell apart from the user's.
            if (opensNewTab(event) && isRowEvent(event)) {
                event.preventDefault();
            }
        },
    };
}

function TableRowWithHover<TData extends RowData>({
    TableRow,
    row,
    props,
    rowRoute,
    onRowClick,
    children,
}: {
    TableRow: React.ComponentType<React.ComponentProps<typeof MuiTableRow>>;
    row: Row<TData>;
    props: Record<string, unknown> & { style?: React.CSSProperties };
    rowRoute: AppLinkOptions | null;
    onRowClick?: () => void;
    children: React.ReactNode;
}): JSX.Element {
    const hoverProps = useRowHoverSelection(row);
    const rowLinkHandlers = useRowLinkHandlers(rowRoute);
    const isClickable = rowRoute !== null || isPresent(onRowClick);

    return (
        <TableRow
            {...props}
            {...hoverProps}
            onClick={onRowClick}
            {...rowLinkHandlers}
            style={{ ...props.style, cursor: isClickable ? 'pointer' : 'default' }}
        >
            {children}
        </TableRow>
    );
}

function isFirstLeafOfGroup<TData extends RowData>(cell: Cell<TData, unknown>): boolean {
    const parent = cell.column.parent;
    if (!parent) return false;
    const leafColumns = parent.getLeafColumns();
    return leafColumns.length > 0 && leafColumns[0].id === cell.column.id;
}

// The design-system Tooltip stops clicks at its popper; a raw MUI Tooltip in the cell opens the row.
// A control, dialog or popover in the cell opens the row unless it stops its own clicks or renders outside the cell.
/** The row's link, through TanStack's `Link`: it preloads on hover and focus, and has `aria-current` on its route. */
const RowLink = linkable(
    styled('a')({
        display: 'flex',
        alignItems: 'center',
        // Fills the cell's height, so the cell's whole content box is the link's target.
        height: '100%',
        color: 'inherit',
        textDecoration: 'none',
        '&:focus-visible': {
            outline: `2px solid ${colorSystem.primary[6]}`,
            outlineOffset: -2,
            borderRadius: 4,
        },
    }),
);

export function InnerTableCell<TData extends RowData, TSharedContext>({
    cell,
    link,
    sharedContext,
    columnRenderOptions,
    displayFormat,
    isFirstCell = false,
}: {
    cell: Cell<TData, unknown>;
    /** Set on the one cell that holds the row's link. */
    link?: AppLinkOptions;
    sharedContext: TSharedContext;
    columnRenderOptions: ColumnRenderOptionsState;
    displayFormat?: DisplayFormat;
    isFirstCell?: boolean;
}): JSX.Element {
    const disableRowClick = cell.column.columnDef.meta?.enableOnRowClick === false;

    // Availability columns follow the app-wide `displayFormat.leadTime`, not a per-column preference.
    const columnId = cell.column.id;
    const columnMeta = cell.column.columnDef.meta;
    const renderOptionConfig = columnMeta?.renderOptionConfig;
    const renderOption =
        columnMeta?.dataType === 'availability'
            ? (displayFormat?.leadTime ?? renderOptionConfig?.default ?? 'default')
            : (columnRenderOptions[columnId] ?? renderOptionConfig?.default ?? 'default');

    const isFirstInGroup = !isFirstCell && isFirstLeafOfGroup(cell);

    const tableCellProps = {
        variant: 'body' as const,
        style: {
            ...getCommonPinningStyles(cell.column),
            textAlign: cell.column.columnDef.meta?.align,
            borderBottom: `1px solid ${colorSystem.neutral[2]}`,
            ...(isFirstInGroup ? { borderLeft: `1px solid ${colorSystem.neutral[3]}` } : {}),
            ...(disableRowClick ? { cursor: 'default' } : {}),
        },
        ...(disableRowClick ? { [DISABLE_ROW_CLICK_ATTRIBUTE]: true } : {}),
        onClick: (e: React.MouseEvent<HTMLDivElement>) => {
            if (disableRowClick) {
                e.stopPropagation();
            }
        },
    };

    if (cell.column.columnDef.meta?.disableTableCell) {
        return (
            <ContentRenderer
                cell={cell}
                sharedContext={sharedContext}
                renderType="generic"
                renderOption={renderOption}
            />
        );
    }

    const content = <ContentRenderer cell={cell} sharedContext={sharedContext} renderOption={renderOption} />;

    return (
        <TableCell {...tableCellProps}>
            {isPresent(link) ? (
                <RowLink {...asLinkProps(link)}>
                    {/* One full-width box, so the content lays out as it would directly in the cell. */}
                    <div style={{ width: '100%', minWidth: 0 }}>{content}</div>
                </RowLink>
            ) : (
                content
            )}
        </TableCell>
    );
}

function defaultIsEmptyValue<TValue>(value: TValue): boolean {
    if (value === null || value === undefined) {
        return true;
    }
    if (typeof value === 'string' && value === '') {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

/**
 * Extracts the placeholder and isEmptyValue function from the emptyCell configuration.
 */
type EmptyCellPlaceholder<TValue> = (() => string) | ((value: TValue) => React.ReactElement);

function extractEmptyCellConfig<TValue>(emptyCell: EmptyCell<TValue> | undefined): {
    placeholder: EmptyCellPlaceholder<TValue> | undefined;
    isEmptyValue: (value: TValue) => boolean;
} {
    if (emptyCell === undefined) {
        return {
            placeholder: undefined,
            isEmptyValue: defaultIsEmptyValue,
        };
    }
    if (typeof emptyCell === 'function') {
        return {
            placeholder: emptyCell as EmptyCellPlaceholder<TValue>,
            isEmptyValue: defaultIsEmptyValue,
        };
    }
    return {
        placeholder: emptyCell.placeholder as EmptyCellPlaceholder<TValue>,
        isEmptyValue: emptyCell.isEmptyValue ?? defaultIsEmptyValue,
    };
}

/**
 * Renders the default empty cell (dash with neutral color).
 */
function renderDefaultEmptyCell(): JSX.Element {
    return (
        <Text variant="inherit" color={colorSystem.neutral[6]}>
            -
        </Text>
    );
}

/**
 * Renders an empty cell based on the configuration.
 */
function renderEmptyCell<TValue>(
    value: TValue,
    placeholder: EmptyCellPlaceholder<TValue> | undefined,
): React.ReactElement {
    if (placeholder === undefined) {
        return renderDefaultEmptyCell();
    }
    // Check if it's a function that returns JSX.Element (takes value parameter)
    // vs a function that returns string (no parameters)
    if (placeholder.length === 0) {
        // It's () => string
        const textPlaceholder = placeholder as () => string;
        return (
            <Text variant="inherit" color={colorSystem.neutral[6]}>
                {textPlaceholder()}
            </Text>
        );
    }
    // It's (value: TValue) => JSX.Element
    return (placeholder as (value: TValue) => JSX.Element)(value);
}

export function ContentRenderer<TData extends RowData, TSharedContext>({
    cell,
    sharedContext,
    renderType: explicitRenderType,
    renderOption,
}: {
    cell: Cell<TData, unknown>;
    sharedContext: TSharedContext;
    renderType?: 'generic';
    renderOption: string;
}): JSX.Element {
    const context = Object.assign(cell.getContext(), { sharedContext, renderOption });
    const columnMeta = cell.column.columnDef.meta;
    const emptyCell = columnMeta?.emptyCell;

    // Check if value is empty and render empty cell if needed
    if (emptyCell !== undefined) {
        const value = cell.getValue();
        const { placeholder, isEmptyValue } = extractEmptyCellConfig(emptyCell);
        if (isEmptyValue(value)) {
            const placeholderElement = renderEmptyCell(value, placeholder);
            const type: RenderType = determineRenderType({
                cell,
                explicitRenderType,
            });

            const commonTextProps = {
                variant: 'inherit' as const,
                style: { display: 'block' },
            };

            switch (type) {
                case 'text':
                    return (
                        <Text {...commonTextProps} showEllipsis={true}>
                            {placeholderElement}
                        </Text>
                    );
                case 'number':
                    return (
                        <Text {...commonTextProps} showEllipsis={true} tabularNums={true}>
                            {placeholderElement}
                        </Text>
                    );
                case 'ellipsisOnly':
                    return (
                        <Text {...commonTextProps} showEllipsis={'ellipsisOnly'}>
                            {placeholderElement}
                        </Text>
                    );
                case 'generic':
                    return placeholderElement;
            }
        }
    }

    const type: RenderType = determineRenderType({
        cell,
        explicitRenderType,
    });

    const commonTextProps = {
        variant: 'inherit' as const,
        style: { display: 'block' },
    };

    switch (type) {
        case 'text':
            return (
                <Text
                    {...commonTextProps}
                    showEllipsis={true}
                    ellipsisText={cell.column.columnDef.meta?.getEllipsisText?.(cell.row.original)}
                >
                    {flexRender(cell.column.columnDef.cell, context)}
                </Text>
            );
        case 'number':
            return (
                <Text {...commonTextProps} showEllipsis={true} tabularNums={true}>
                    {flexRender(cell.column.columnDef.cell, context)}
                </Text>
            );
        case 'ellipsisOnly':
            return (
                <Text {...commonTextProps} showEllipsis={'ellipsisOnly'}>
                    {flexRender(cell.column.columnDef.cell, context)}
                </Text>
            );
        case 'generic':
            return flexRender(cell.column.columnDef.cell, context) as JSX.Element;
    }
}

function determineRenderType<TData extends RowData>({
    cell,
    explicitRenderType,
}: {
    cell: Cell<TData, unknown>;
    explicitRenderType?: 'generic';
}): RenderType {
    const columnMeta = assertPresent(cell.column.columnDef.meta);
    if (isPresent(explicitRenderType)) {
        return explicitRenderType;
    }

    if (isPresent(columnMeta.renderType)) {
        return parseOptionsValuesOrFunc(columnMeta.renderType, cell.row.original);
    }

    switch (columnMeta?.dataType) {
        case 'text':
            return 'text';
        case 'number':
        case 'date':
        case 'monetaryValue':
        case 'quantity':
            return 'number';
        default:
            return 'generic';
    }
}
